import React from 'react';
import { Link } from 'react-router-dom';
import { PageHeader } from '../components/common/PageHeader';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/Tabs';
import { PackageTiers } from '../components/outreach/PackageTiers';
import { ChannelBoard } from '../components/outreach/ChannelBoard';
import { CampaignCalendar } from '../components/outreach/CampaignCalendar';
import { ReachAnalytics } from '../components/outreach/ReachAnalytics';
import { EmptyState } from '../components/common/EmptyState';
import { campaigns, packageTiers } from '../data/outreach';
import { formatDate } from '../utils/format';
import { cn } from '../utils/cn';
import type { PackageTierId } from '../types/outreach';
export function Outreach() {
  const [tierFilter, setTierFilter] = React.useState<PackageTierId | undefined>(undefined);
  const visible = tierFilter ? campaigns.filter(c => c.tier === tierFilter) : campaigns;
  const [selectedId, setSelectedId] = React.useState(campaigns[0]?.id ?? '');
  const selected = visible.find(c => c.id === selectedId) ?? visible[0];
  const assignedCounts = packageTiers.reduce((acc, tier) => ({
    ...acc,
    [tier.id]: campaigns.filter(campaign => campaign.tier === tier.id).length
  }), {} as Record<PackageTierId, number>);
  return <div data-id="element-1438" data-mp="1s6tjgxvg1ckx">
      <PageHeader eyebrow="Delivery · International track" title="Outreach & amplification" description="Approved International events are pushed across the UAE ecosystem on a paid package tier, with delivery measured against the impression guarantee." data-id="element-1439" data-mp="1s508j6dss674" />

      <div className="space-y-4 p-6" data-id="element-1440" data-mp="1e5h5ut3r9n27">
        <PackageTiers assignedCounts={assignedCounts} selected={tierFilter} onSelect={tier => setTierFilter(current => current === tier ? undefined : tier)} data-id="element-1441" data-mp="1e6ir1qehjpxn" />

        {visible.length === 0 ? <EmptyState title="No campaigns on this tier" description="Clear the tier filter to see all live outreach campaigns." data-id="element-1442" data-mp="33lglmt1vqaqt" /> : <div className="grid gap-4 xl:grid-cols-[300px_1fr]" data-id="element-1443" data-mp="11u7y3butv3io">
            <nav aria-label="Live campaigns" className="space-y-2" data-id="element-1444" data-mp="16oc7s2vh9png">
              <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground" data-id="element-1445" data-mp="rymrt1iq7h34">
                Live campaigns ({visible.length})
              </p>
              {visible.map(campaign => <button key={campaign.id} type="button" onClick={() => setSelectedId(campaign.id)} aria-current={selected?.id === campaign.id} className={cn('w-full rounded-sm border px-3 py-2.5 text-left transition-colors', selected?.id === campaign.id ? 'border-uae-green bg-uae-green text-white' : 'border-border bg-card hover:bg-muted')} data-id="element-1446" data-mp="1knu1mwnmrt3o">
                  <p className="text-[13px] font-medium leading-snug" data-id="element-1447" data-mp="3su5mbe7vzzba">{campaign.eventName}</p>
                  <p className={cn('mt-0.5 truncate text-[11px]', selected?.id === campaign.id ? 'text-white/70' : 'text-muted-foreground')} data-id="element-1448" data-mp="3nv2u4ud09nkq">
                    {campaign.entityName}
                  </p>
                  <p className={cn('mt-1 text-[11px] capitalize', selected?.id === campaign.id ? 'text-white/70' : 'text-muted-foreground')} data-id="element-1449" data-mp="3a3egunr0st26">
                    {campaign.tier} · {formatDate(campaign.startsOn)} —{' '}
                    {formatDate(campaign.endsOn)}
                  </p>
                </button>)}
            </nav>

            {selected && <div className="space-y-4" data-id="element-1450" data-mp="2ratvdrdnhq8e">
                <div className="flex flex-wrap items-center justify-between gap-3 rounded-sm border border-border bg-card px-4 py-3" data-id="element-1451" data-mp="2md0oew4iws72">
                  <div data-id="element-1452" data-mp="1pojbj2k6fm6n">
                    <p className="text-[13px] font-semibold" data-id="element-1453" data-mp="1toa1x55sk0an">{selected.eventName}</p>
                    <p className="text-[11px] text-muted-foreground" data-id="element-1454" data-mp="1tlxl4vbrrvz6">
                      {selected.emirate} · {selected.tier.toUpperCase()} package ·{' '}
                      {selected.placements.length} placements
                    </p>
                  </div>
                  <Link to={`/applications/${selected.applicationId}`} className="rounded-sm border border-border px-3 py-1.5 text-[12px] font-medium transition-colors hover:bg-muted" data-id="element-1455" data-mp="28ceoik7ysvyp">
                    Open application record
                  </Link>
                </div>

                <Tabs defaultValue="channels" data-id="element-1456" data-mp="2malab1zn35i0">
                  <TabsList data-id="element-1457" data-mp="n0wq6csqa9hp">
                    <TabsTrigger value="channels" data-id="element-1458" data-mp="n1o7qjphq6oz">Channel distribution</TabsTrigger>
                    <TabsTrigger value="calendar" data-id="element-1459" data-mp="rzpv3gty8s69">Campaign calendar</TabsTrigger>
                    <TabsTrigger value="analytics" data-id="element-1460" data-mp="kggtwavljzsa">Reach analytics</TabsTrigger>
                  </TabsList>
                  <TabsContent value="channels" className="mt-4" data-id="element-1461" data-mp="fnx4mgnjrg7n">
                    <ChannelBoard placements={selected.placements} data-id="element-1462" data-mp="apccl4ig82ua" />
                  </TabsContent>
                  <TabsContent value="calendar" className="mt-4" data-id="element-1463" data-mp="5qsooykaogza">
                    <CampaignCalendar entries={selected.calendar} data-id="element-1464" data-mp="5og38bfbvohh" />
                  </TabsContent>
                  <TabsContent value="analytics" className="mt-4" data-id="element-1465" data-mp="13c6v3vvpgs54">
                    <ReachAnalytics campaign={selected} data-id="element-1466" data-mp="yghtzo41f36p" />
                  </TabsContent>
                </Tabs>
              </div>}
          </div>}
      </div>
    </div>;
}